import { logoutCurrentUser } from '@Pages/pageRuntime/lib/logout';

import type { SdkAuth, SdkAuthConfig, SdkRouter } from './types';

/**
 * Endpoint that verifies an identifier/password pair against a workspace
 * authenticatable resource. The route resolves the identifier field id and
 * handles CSRF, session regeneration, and rate limiting server-side (see
 * `VerifyPassword`), then issues an Inertia-compatible redirect the router follows
 * (a 302 for same-origin workspace pages; a full `X-Inertia-Location` redirect
 * for cross-origin destinations).
 */
function verifyPasswordUrl(resourceId: string): string {
  return `/auth/resource/${resourceId}/verify/password`;
}

/**
 * Endpoint that emails a one-time sign-in link. Only mounted for resources that
 * enabled magic link login; the route resolves the identifier field id and answers
 * identically whether or not an account exists (see `SendMagicLink`).
 */
function magicLinkUrl(resourceId: string): string {
  return `/auth/resource/${resourceId}/magic-link`;
}

/**
 * Endpoint that emails a password reset link. Only meaningful for resources with a
 * password field; like the magic link route it answers identically whether or not an
 * account exists (see `SendPasswordResetLink`).
 */
function forgotPasswordUrl(resourceId: string): string {
  return `/auth/resource/${resourceId}/forgot-password`;
}

/**
 * Build the `Sdk.auth` namespace from the shared router and the server-injected
 * auth config. Logout uses the shared logout helper (`logoutCurrentUser`) but posts
 * through the shared router so application request handling stays consistent.
 */
export function createAuth(router: SdkRouter, config: SdkAuthConfig | null | undefined): SdkAuth {
  const resources = config?.resources ?? [];
  const defaultResourceId = config?.resourceId ?? null;
  const currentPageId = config?.currentPageId ?? null;

  const resolveResourceId = (method: string, resourceId: string | undefined): string => {
    const targetResourceId = resourceId ?? defaultResourceId;

    if (!targetResourceId) {
      throw new Error(
        `Sdk.auth.${method}: this workspace has no single authenticatable resource, so a resourceId is required. Pass one via Sdk.auth.${method}({ ..., resourceId }) — the available resources are listed on Sdk.auth.resources.`
      );
    }

    return targetResourceId;
  };

  return {
    resourceId: defaultResourceId,
    resources,
    twoFactor: config?.twoFactor ?? null,

    login: ({ identifier, password, resourceId, redirectPage, returnTo, twoFactor }, options) => {
      router.post(
        verifyPasswordUrl(resolveResourceId('login', resourceId)),
        {
          identifier,
          password,
          embedded: true,
          ...(redirectPage ? { redirectPage } : {}),
          ...(returnTo === 'current' ? { returnTo } : {}),
          ...(twoFactor === 'inline' ? { twoFactor } : {})
        },
        options
      );
    },

    sendMagicLink: ({ identifier, resourceId, redirectPage, returnTo, twoFactor }, options) => {
      router.post(
        magicLinkUrl(resolveResourceId('sendMagicLink', resourceId)),
        {
          identifier,
          ...(redirectPage ? { redirectPage } : {}),
          ...(returnTo === 'current' && currentPageId ? { returnTo, currentPageId } : {}),
          ...(twoFactor === 'inline' && currentPageId ? { twoFactor, currentPageId } : {})
        },
        options
      );
    },

    sendPasswordResetLink: ({ identifier, resourceId, returnTo, twoFactor }, options) => {
      router.post(
        forgotPasswordUrl(resolveResourceId('sendPasswordResetLink', resourceId)),
        {
          identifier,
          ...(returnTo === 'current' && currentPageId ? { returnTo, currentPageId } : {}),
          ...(twoFactor === 'inline' && currentPageId ? { twoFactor, currentPageId } : {})
        },
        options
      );
    },

    logout: (options) => {
      const callerOnStart = options?.onStart;

      logoutCurrentUser({
        post: (url, data, logoutOptions) =>
          router.post(url, data, {
            ...options,
            onStart: (visit: unknown) => {
              logoutOptions.onStart();

              if (typeof callerOnStart === 'function') {
                callerOnStart(visit);
              }
            }
          })
      });
    }
  };
}
